import React, { lazy, useEffect } from "react";
import { useRoutes, RouteObject, useNavigate, To, useMatch } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Clarity from "@microsoft/clarity";
import { actions as sharedActions, selectors as sharedSelectors } from "@shared/store";
import { NamesOfParentRoutes } from "@shared/constants";
import useNotification from "@shared/hooks/NotificationHook";
import { Loadable, LoadingIndicator } from "@shared/components";
import OverlayWrapper from "@shared/components/OverlayWrapper/OverlayWrapper";
import ModalWrapper from "@shared/components/ModalWrapper/ModalWrapper";
import ProgressScreenWrapper from "@shared/components/ProgressScreenWrapper/ProgressScreenWrapper";
import { getActiveAccount } from "@containers/Account/store/selectors";
import { userStorage } from "@shared/utils";
import { checkIfAdmin } from "@shared/utils/ACL";
import { createVitallyUser } from "@shared/utils/vitally";
import { NavigationScroll } from "@shared/components/NavigationScroll";
import { ReactComponent as Logo } from "@assets/files/new/logo.svg";
import classnames from "classnames";
import { Integrations } from "@shared/components/Integrations";
import { MigrationBanner } from "@shared/components/MigrationBanner";

import { NotFound } from "../../shared/components";
import { AuthGuard, GuestGuard, OAuthGuard } from "../../shared/guards";
import config from "../../config";

import "./index.scss";

const AuthContainer = Loadable(lazy(() => import("../Auth/containers/AuthContainer/AuthContainer")));
const DashboardContainer = Loadable(
  lazy(() => import("@containers/Dashboard/containers/DashboardContainer/DashboardContainer")),
);

const routes: RouteObject[] = [
  {
    path: `${NamesOfParentRoutes.DASHBOARD}*`,
    element: (
      <AuthGuard>
        <DashboardContainer />
      </AuthGuard>
    ),
  },
  {
    path: NamesOfParentRoutes.OAUTH,
    element: <OAuthGuard />,
  },
  {
    path: `${NamesOfParentRoutes.AUTH}/*`,
    element: (
      <GuestGuard>
        <AuthContainer />
      </GuestGuard>
    ),
  },
  {
    path: "*",
    element: <NotFound />,
  },
];

const App = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const notification = useSelector(sharedSelectors.getNotification());
  const navigateURL = useSelector(sharedSelectors.getNavigateURL());
  const loading = useSelector(sharedSelectors.getLoading());
  const currentUser = useSelector(sharedSelectors.getUserDetails());
  const account = useSelector(getActiveAccount());

  useEffect(() => {
    Clarity.init(config.clarity.id);
  }, []);

  // When the user navigates to a lazy-loaded page after the deployment, the old app version tries to load a chunk that no longer exists.
  useEffect(() => {
    const handleChunkError = (error: ErrorEvent) => {
      if (error.message?.includes("Loading chunk")) {
        window.location.reload();
      }
    };

    window.addEventListener("error", handleChunkError);

    return () => {
      window.removeEventListener("error", handleChunkError);
    };
  }, []);

  useEffect(() => {
    createVitallyUser(currentUser, account);
    if (currentUser && account) {
      Clarity.setTag("user_id", currentUser.id.toString());
      Clarity.setTag("account_id", account.id.toString());
    }
  }, [currentUser, account]);

  useEffect(() => {
    if (navigateURL) {
      navigate(navigateURL.to as To, navigateURL.options);
      dispatch(sharedActions.navigate(null));
    }
  }, [navigateURL, dispatch, navigate]);

  useEffect(() => {
    const user = userStorage.get();

    if (user) {
      dispatch(sharedActions.getUserDetails.request());
    }
  }, [dispatch]);

  const content = useRoutes(routes);

  useNotification(notification);

  const isAuth = !!useMatch(`${NamesOfParentRoutes.AUTH}*`);

  return (
    <>
      {!isAuth && <ModalWrapper />}
      {!isAuth && <OverlayWrapper />}
      {!isAuth && <Integrations />}
      <ProgressScreenWrapper />
      <NavigationScroll />
      <div className="app-wrapper">
        {currentUser && !checkIfAdmin(currentUser) && <MigrationBanner />}
        <div
          className={classnames("App", {
            new: isAuth,
          })}
        >
          {loading && (
            <>
              {isAuth ? (
                <div className="loader">
                  <Logo />
                  <div className="processing">Processing...</div>
                </div>
              ) : (
                <LoadingIndicator />
              )}
            </>
          )}
          <div className={classnames("content-wrapper", { loading })}>{content}</div>
        </div>
      </div>
    </>
  );
};

export default App;
